import { StyleProp, ViewStyle } from "react-native";
import { FC, memo, useEffect } from "react";
import { useUnistyles, StyleSheet } from "react-native-unistyles";
import Animated, { useSharedValue, useAnimatedStyle, withSpring } from "react-native-reanimated";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { AiIconLg } from "@src/components/icons/AiIconLg";
import { PressableScale } from "pressto";

type AiFloatingActionButtonProps = {
  onPress: () => void;
  style?: StyleProp<ViewStyle>;
  variant?: "default" | "tab";
};

const AiFloatingActionButton: FC<AiFloatingActionButtonProps> = memo(({ onPress, style, variant = "default" }) => {
  const { theme } = useUnistyles();
  const { bottom } = useSafeAreaInsets();
  const translateY = useSharedValue(100);

  useEffect(() => {
    translateY.value = withSpring(0, {
      damping: 35,
      stiffness: 250,
    });
  }, [translateY]);

  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateY: translateY.value }],
    };
  });

  const bottomOffset =
    bottom +
    (variant === "tab" ?
      0 // on tab screens, sit just above the tab bar
    : theme.spacing["2xl"]); // in full-screen contexts, sit a bit higher

  return (
    <Animated.View
      style={[
        styles.container,
        {
          bottom: bottomOffset,
        },
        style,
        animatedStyle,
      ]}
    >
      <PressableScale onPress={onPress} style={styles.button} hitSlop={8}>
        <AiIconLg size={56} />
      </PressableScale>
    </Animated.View>
  );
});

const styles = StyleSheet.create((theme) => ({
  container: {
    position: "absolute",
    right: theme.spacing["4xl"],
    bottom: theme.spacing["3xl"],
  },
  button: {
    width: 56,
    height: 56,
    borderRadius: 28,
    shadowColor: theme.colors.black.default,
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 8,
  },
}));
export default AiFloatingActionButton;
